import React, { useEffect, useMemo, useRef, useState } from 'react'
import intl from 'react-intl-universal'
import _ from 'lodash'
import { Input } from 'reactstrap'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const styles = require('../CustomEmbedSettingsModal.styles.scss')
// eslint-disable-next-line @typescript-eslint/no-var-requires
const locale = require('./locale.json')

interface LocaleIcon {
  iconName: string
  id: string
}

interface SelectIconEmbedProps {
  value: string | undefined
  onIconSelected: (iconId: string) => void
}

export const SelectIconEmbed: React.FC<SelectIconEmbedProps> = ({
  value,
  onIconSelected,
}) => {
  const initialValue = value
    ? ({ iconName: '', id: value } as LocaleIcon)
    : undefined
  const [selectedIcon, setSelectedIcon] = useState<LocaleIcon | undefined>(
    initialValue
  )
  const [showSearchContainer, setSetshowSearchContainer] = useState(false)

  return (
    <div className={styles.root}>
      <div className="icon-lable">
        {intl.get('CustomEmbedSettingsModal.fields.icon.label')}
      </div>
      {selectedIcon && (
        <div
          className="selected-icon-container"
          data-testid="selected-icon-container"
        >
          <span
            className={`fa fa-${selectedIcon.id} selected-icon`}
            aria-label={selectedIcon.iconName}
            data-testid={`selected-icon-${selectedIcon.id}`}
          ></span>
          <span className="selected-icon-name">
            {selectedIcon.iconName || selectedIcon.id}
          </span>
        </div>
      )}
      {!showSearchContainer && (
        <button
          type="button"
          className="btn btn-override-secondary-outline"
          onClick={() => setSetshowSearchContainer(true)}
          data-testid="select-icon-button"
          aria-label="Select Icon"
        >
          {intl.get('CustomEmbedSettingsModal.fields.icon.chooseIcon')}
        </button>
      )}
      {showSearchContainer && (
        <SearchContainer
          onIconSelected={(iconId) => {
            setSelectedIcon(iconId)
            setSetshowSearchContainer(false)
            onIconSelected(iconId.id)
          }}
        />
      )}
    </div>
  )
}

interface SearchContainerProps {
  onIconSelected: (iconId: LocaleIcon) => void
}

export const SearchContainer: React.FC<SearchContainerProps> = ({
  onIconSelected,
}) => {
  const [searchValue, setSearchValue] = React.useState('')
  const [iconList, setIconList] = useState([] as LocaleIcon[])
  const language = navigator.language.split('-')[0] || 'en'
  const localeArray = useMemo(
    () => (locale[language] || locale.en) as LocaleIcon[],
    [language]
  )
  const [firstSearch, setFirstSearch] = useState(false)

  const debounceSearch = useRef(
    _.debounce(
      (value) => {
        setFirstSearch(true)
        const iconList = [] as LocaleIcon[]
        localeArray.forEach((item) => {
          if (item.iconName.includes(value.toLowerCase())) {
            iconList.push(item)
          }
        })
        setIconList(iconList)
      },
      700,
      {
        trailing: true,
      }
    )
  )

  useEffect(() => {
    debounceSearch.current(searchValue)
  }, [searchValue, localeArray])

  return (
    <div className="search-container">
      <Input
        type="text"
        name="search-icon"
        value={searchValue}
        placeholder="Search icon"
        className="input-primary"
        data-testid="search-icon-input"
        onChange={(event) => {
          setSearchValue(event.target.value)
        }}
        onKeyPress={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            debounceSearch.current.flush()
          }
        }}
      />
      {iconList.length > 0 && (
        <div className="icons-container">
          <div className="embed-icon-list">
            {iconList.map((item) => (
              <button
                type="button"
                key={item.id}
                data-toggle="tooltip"
                data-delay='{"show":"500"}'
                title={item.id}
                data-testid={`icon-button-${item.id}`}
                onClick={() => onIconSelected(item)}
                className="embed-icon-button"
              >
                <span
                  className={`fa fa-${item.id}`}
                  aria-label={item.id}
                ></span>
              </button>
            ))}
          </div>
        </div>
      )}
      {iconList.length === 0 && firstSearch && (
        <div className="no-results" data-testid="no-results">
          {intl.get('CustomEmbedSettingsModal.errors.noResults')}
        </div>
      )}
    </div>
  )
}
